/**
 * BuildVersion — the build stamp of the app as a small mono pill with a copy
 * icon (user menu · «versió de la build»). Clicking it copies the text, so the
 * exact build can be pasted into a bug report; for two seconds the icon turns
 * into a check and the accessible name says «copiat».
 *
 * The text (`v AAAAMM-DD-HHmm`) and both labels come from the app: the design
 * system carries no strings. If the browser refuses the clipboard (insecure
 * context, permission denied) nothing happens and the text stays readable.
 */

import React from 'react';
import { useTheme } from '../theme/index.js';

export interface BuildVersionProps {
  /** The text shown and copied, already formatted (e.g. `v 202610-23-1715`). */
  readonly version: string;
  /** Accessible name / tooltip while idle («Copia la versió»). */
  readonly copyLabel: string;
  /** Accessible name / tooltip for two seconds after copying («Versió copiada»). */
  readonly copiedLabel: string;
  readonly style?: React.CSSProperties;
}

const COPIED_MS = 2000;

const ICON_PROPS = {
  width: 15,
  height: 15,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
} as const;

export function BuildVersion(props: BuildVersionProps): React.ReactElement {
  const { version, copyLabel, copiedLabel, style } = props;
  const theme = useTheme();
  const { space } = theme.spacing;
  const [copied, setCopied] = React.useState(false);
  const [hover, setHover] = React.useState(false);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  const onCopy = (): void => {
    const clipboard: Clipboard | undefined =
      typeof navigator === 'undefined' ? undefined : navigator.clipboard;
    if (clipboard === undefined) return;
    clipboard.writeText(version).then(
      () => {
        setCopied(true);
        if (timer.current !== null) clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), COPIED_MS);
      },
      () => undefined,
    );
  };

  const label = copied ? copiedLabel : copyLabel;

  return (
    <button
      type="button"
      onClick={onCopy}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={label}
      aria-label={`${label}: ${version}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: space[3],
        width: '100%',
        padding: `${space[2]}px ${space[4]}px`,
        border: `1px solid ${theme.color.border}`,
        borderRadius: theme.radii.radius.pill,
        backgroundColor: hover ? theme.color.hover : theme.color.surfaceRaised,
        color: theme.color.textSecondary,
        fontFamily: theme.typography.family.mono,
        fontSize: theme.typography.size.caption,
        lineHeight: '16px',
        textAlign: 'left',
        cursor: 'pointer',
        ...style,
      }}
    >
      <span style={{ userSelect: 'text', whiteSpace: 'nowrap' }}>{version}</span>
      <span
        style={{
          display: 'inline-flex',
          flex: 'none',
          color: copied ? theme.color.successText : theme.color.textTertiary,
        }}
      >
        {copied ? (
          <svg {...ICON_PROPS}>
            <path d="M5 12.5l4.5 4.5L19 7.5" />
          </svg>
        ) : (
          <svg {...ICON_PROPS}>
            <rect x="9" y="9" width="11" height="11" rx="2.5" />
            <path d="M5 15V6.5A2.5 2.5 0 0 1 7.5 4H15" />
          </svg>
        )}
      </span>
    </button>
  );
}
